/* Savage State product page: live 3D product in the gallery (sections/ss-pdp-main.liquid).
   Greatness Extreme V2 renders as the traced tub with its label wrap per flavor;
   TestoFORGE renders as the carton with its six printed panels. Artwork comes from
   the section's "3D tub label" / "3D carton" blocks, all hosted in Shopify Files. */
import * as THREE from 'https://unpkg.com/three@0.184.0/build/three.module.js';

const host = document.querySelector('[data-ss3d]');
if (host && !host.dataset.glReady) {
  host.dataset.glReady = '1';
  try { boot(host); } catch (e) { off(); }
}

function off() {
  const st = host && host.closest('[data-ss-stage]');
  if (st) st.setAttribute('data-gl', 'off');
  document.dispatchEvent(new CustomEvent('ss:gl-off'));
}

function boot(host) {
  const main = host.closest('[data-ss-main]');
  const cfgEl = main && main.querySelector('[data-ss3d-config]');
  const cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}');
  if (cfg.shape !== 'tub' && cfg.shape !== 'box') { off(); return; }

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  } catch (e) { off(); return; }
  const mobile = matchMedia('(max-width: 760px)').matches;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 1.75 : 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.94;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  host.appendChild(renderer.domElement);
  renderer.domElement.addEventListener('webglcontextlost', (e) => { e.preventDefault(); off(); });

  const scene = new THREE.Scene();
  scene.environment = studioEnv(renderer);
  /* grunge rig: neutral hard key + dim white kickers, no coloured bloom */
  scene.add(new THREE.HemisphereLight(0xffffff, 0x101014, 0.30));
  const key = new THREE.DirectionalLight(0xffffff, 1.5); key.position.set(3, 6, 5); scene.add(key);
  const rim = new THREE.DirectionalLight(0xffffff, 0.85); rim.position.set(4, 1.6, -3.2); scene.add(rim);
  const acc = new THREE.DirectionalLight(0xffffff, 0.35); acc.position.set(-4.5, 0.6, -2.4); scene.add(acc);

  const maxAniso = renderer.capabilities.getMaxAnisotropy();
  const loader = new THREE.TextureLoader();
  loader.setCrossOrigin('anonymous');
  function load(src, done) {
    if (!src) return;
    const url = src.indexOf('//') === 0 ? window.location.protocol + src : src;
    loader.load(url, (t) => {
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = maxAniso;
      done(t);
    }, undefined, () => {});
  }

  const obj = cfg.shape === 'tub' ? buildTub(cfg, load) : buildBox(cfg, load);
  scene.add(obj.group);

  /* ---- camera: fit the object, pulling back on narrow boxes ---- */
  const cam = new THREE.PerspectiveCamera(24, 1, 0.001, 50);
  const bb = new THREE.Box3().setFromObject(obj.group);
  const size = bb.getSize(new THREE.Vector3());
  const ctr = bb.getCenter(new THREE.Vector3());
  function frame() {
    const w = host.clientWidth || 1, h = host.clientHeight || 1;
    renderer.setSize(w, h, false);
    cam.aspect = w / h;
    const t = Math.tan((cam.fov * Math.PI) / 360);
    const halfH = size.y / 2, halfW = Math.max(size.x, size.z) * 0.62;
    const dist = Math.max(halfH / t, halfW / (t * cam.aspect)) * obj.pad;
    cam.position.set(dist * 0.06, ctr.y + size.y * 0.12, dist);
    cam.lookAt(0, ctr.y, 0);
    cam.updateProjectionMatrix();
  }
  frame();
  if ('ResizeObserver' in window) new ResizeObserver(frame).observe(host);
  else window.addEventListener('resize', frame);

  /* ---- drag to spin, idle drift otherwise ---- */
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hint = main.querySelector('[data-ss-hint]');
  let spin = obj.rest, vel = 0, drag = false, lastX = 0, touched = false;
  host.addEventListener('pointerdown', (e) => {
    drag = true; touched = true; lastX = e.clientX;
    try { host.setPointerCapture(e.pointerId); } catch (err) {}
    if (hint) hint.style.opacity = '0';
  });
  host.addEventListener('pointermove', (e) => {
    if (!drag) return;
    const dx = e.clientX - lastX; lastX = e.clientX;
    spin += dx * 0.008; vel = dx * 0.008;
  });
  const stop = () => { drag = false; };
  host.addEventListener('pointerup', stop);
  host.addEventListener('pointercancel', stop);

  /* render only while the stage is on screen and the tab is visible */
  let visible = true;
  if ('IntersectionObserver' in window) {
    new IntersectionObserver((es) => { es.forEach((e) => { visible = e.isIntersecting; }); }, { threshold: 0 }).observe(host);
  }
  let last = 0;
  function tick(ts) {
    requestAnimationFrame(tick);
    if (!visible || document.hidden || host.offsetParent === null) return;
    if (ts - last < 14) return;
    last = ts;
    const s = ts * 0.001;
    if (!drag) {
      spin += vel;
      vel *= 0.94;
      if (!touched && !reduce) spin = obj.rest + Math.sin(s * 0.28) * obj.sway;
    }
    obj.group.rotation.y = spin;
    obj.group.position.y = reduce ? 0 : Math.sin(s * 0.62) * obj.bob;
    obj.group.rotation.z = reduce ? 0 : Math.sin(s * 0.44) * 0.006;
    renderer.render(scene, cam);
  }
  requestAnimationFrame(tick);
  renderer.render(scene, cam);

  document.addEventListener('ss:variant', (e) => { if (obj.setVariant) obj.setVariant(e.detail && e.detail.id); });
}

/* ---------------- Greatness Extreme V2 tub ---------------- */
function buildTub(cfg, load) {
  const V = (r, y) => new THREE.Vector2(r, y);
  const arcPts = (cx, cy, rad, a0, a1, seg) => {
    const o = [];
    for (let i = 0; i <= seg; i++) { const a = a0 + (a1 - a0) * (i / seg); o.push(V(cx + rad * Math.cos(a), cy + rad * Math.sin(a))); }
    return o;
  };
  const D2R = Math.PI / 180;
  const R_BODY = 0.05250, R_LID = 0.05400;
  const LID_TOP = 0.09885, LID_BOT = 0.08208, LID_CR = 0.00200, LID_CR_CX = R_LID - LID_CR;
  const LABEL_BOTTOM = 0.01265, LABEL_H = LID_BOT - LABEL_BOTTOM;
  const jarProfile = [
    V(0, 0.00420), V(0.016, 0.00400), V(0.028, 0.00330), V(0.035, 0.00210),
    V(0.039, 0.00070), V(0.04050, 0),
    V(0.04192, 0.00112), V(0.04515, 0.00208), V(0.04587, 0.00303),
    V(0.04791, 0.00495), V(0.04967, 0.00687), V(0.05062, 0.00878),
    V(0.05106, 0.01070), V(0.05180, 0.01135), V(R_BODY, 0.01230),
    V(R_BODY, 0.08300),
    V(0.05180, 0.08420), V(0.05120, 0.08560), V(0.05120, 0.08900),
    V(0.04980, 0.08900), V(0.04980, 0.01250),
    V(0.04780, 0.00950), V(0.036, 0.00830), V(0.016, 0.00805), V(0, 0.00800)];
  const lidProfile = [
    V(0, LID_TOP), V(0.024, 0.09880), V(0.040, 0.09872), V(0.0480, 0.09866),
    V(0.05200, 0.09862),
    ...arcPts(LID_CR_CX, LID_TOP - LID_CR, LID_CR, 90 * D2R, 0, 10),
    V(R_LID, 0.08760), V(0.05372, 0.08700), V(0.05372, 0.08660), V(R_LID, 0.08610),
    V(R_LID, 0.08268), V(0.05378, 0.08222), V(0.05330, LID_BOT),
    V(0.05270, 0.08230), V(0.05270, 0.09400),
    V(0.05180, 0.09480), V(0.020, 0.09520), V(0, 0.09540)];

  const RADIAL = 160;
  const matShell = new THREE.MeshPhysicalMaterial({ name: 'jar_black', color: 0x080809,
    roughness: 0.72, metalness: 0, clearcoat: 0.35, clearcoatRoughness: 0.28, envMapIntensity: 0.72 });
  const matLid = new THREE.MeshPhysicalMaterial({ name: 'lid_black', color: 0x050506,
    roughness: 0.66, metalness: 0, clearcoat: 0.3, clearcoatRoughness: 0.3, envMapIntensity: 0.6 });
  const matLabel = new THREE.MeshPhysicalMaterial({ name: 'label_wrap', color: 0x0b0b0d,
    roughness: 0.78, metalness: 0, clearcoat: 0.25, clearcoatRoughness: 0.34, envMapIntensity: 0.5 });

  const jar = new THREE.Mesh(new THREE.LatheGeometry(jarProfile, RADIAL), matShell);
  const lid = new THREE.Mesh(new THREE.LatheGeometry(lidProfile.slice().reverse(), RADIAL), matLid);
  const label = new THREE.Mesh(new THREE.CylinderGeometry(R_BODY + 0.00012, R_BODY + 0.00012, LABEL_H, RADIAL, 1, true), matLabel);
  label.position.y = LABEL_BOTTOM + LABEL_H / 2;
  const group = new THREE.Group();
  group.add(jar, label, lid);

  /* variant id -> wrap url: exact variant-name match, else a blank (all variants) block, else the first */
  const wraps = cfg.wraps || [];
  const anyWrap = wraps.find((w) => !w.t) || wraps[0];
  const byId = {};
  (cfg.variants || []).forEach((v) => {
    const w = wraps.find((x) => x.t && x.t === v.t) || anyWrap;
    if (w) byId[String(v.id)] = w.src;
  });
  const tex = {};
  let want = String(cfg.v0 || '');
  function show(src) {
    const t = tex[src];
    if (!t || t === 'loading') return;
    matLabel.map = t;
    matLabel.color.setHex(0xffffff);
    matLabel.needsUpdate = true;
  }
  function fetchWrap(src, then) {
    if (!src || tex[src]) { if (then) then(); return; }
    tex[src] = 'loading';
    load(src, (t) => {
      t.wrapS = THREE.RepeatWrapping;
      t.wrapT = THREE.ClampToEdgeWrapping;
      t.offset.x = 0.475;       /* GREATNESS panel faces the camera at rest */
      tex[src] = t;
      if (byId[want] === src) show(src);
      if (then) then();
    });
  }
  /* current flavor first, the rest once it has landed */
  fetchWrap(byId[want] || (anyWrap && anyWrap.src), () => {
    Object.keys(byId).forEach((id) => fetchWrap(byId[id]));
  });

  return {
    group, pad: 1.48, rest: -0.22, sway: 0.34, bob: 0.0018,
    setVariant(id) {
      want = String(id || '');
      const src = byId[want];
      if (!src) return;
      if (tex[src] && tex[src] !== 'loading') show(src); else fetchWrap(src);
    }
  };
}

/* ---------------- TestoFORGE carton ---------------- */
function buildBox(cfg, load) {
  /* measured off the R13 dieline, metres, base at y = 0 */
  const IN = 0.0254;
  const BW = 2.674 * IN, BD = 2.699 * IN, BH = 5.139 * IN;
  const P = cfg.panels || {};
  const stock = () => new THREE.MeshPhysicalMaterial({ color: 0x141416, roughness: 0.46, metalness: 0,
    clearcoat: 0.62, clearcoatRoughness: 0.14, envMapIntensity: 0.78 });
  /* BoxGeometry material order: +X, -X, +Y, -Y, +Z, -Z */
  const order = ['right', 'left', 'top', 'bottom', 'front', 'back'];
  const mats = order.map(() => stock());
  order.forEach((k, i) => {
    load(P[k], (t) => {
      t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
      if (k === 'top') { t.center.set(0.5, 0.5); t.rotation = Math.PI; }
      mats[i].map = t;
      mats[i].color.setHex(0xffffff);
      mats[i].needsUpdate = true;
    });
  });
  const box = new THREE.Mesh(new THREE.BoxGeometry(BW, BH, BD, 1, 1, 1), mats);
  box.position.y = BH / 2;
  /* soft crush along the four vertical folds instead of a razor CG edge */
  const crease = new THREE.MeshPhysicalMaterial({ color: 0x101013, roughness: 0.55, clearcoat: 0.35, transparent: true, opacity: 0.42 });
  const group = new THREE.Group();
  group.add(box);
  [[BW / 2, BD / 2], [-BW / 2, BD / 2], [BW / 2, -BD / 2], [-BW / 2, -BD / 2]].forEach(([x, z]) => {
    const c = new THREE.Mesh(new THREE.CylinderGeometry(0.0009, 0.0009, BH * 0.999, 6, 1), crease);
    c.position.set(x, BH / 2, z);
    group.add(c);
  });
  return { group, pad: 1.4, rest: -0.48, sway: 0.3, bob: 0.0024 };
}

/* ---------------- studio softbox environment ---------------- */
function studioEnv(renderer) {
  const w = 1024, h = 512, c = document.createElement('canvas');
  c.width = w; c.height = h;
  const g = c.getContext('2d');
  const grd = g.createLinearGradient(0, 0, 0, h);
  grd.addColorStop(0.00, '#3c3b3a'); grd.addColorStop(0.22, '#4a4947');
  grd.addColorStop(0.44, '#8f8d88'); grd.addColorStop(0.54, '#4e4c4a');
  grd.addColorStop(1.00, '#121213');
  g.fillStyle = grd; g.fillRect(0, 0, w, h);
  const softbox = (cx, cy, bw, bh, a) => {
    const r = g.createRadialGradient(cx, cy, 0, cx, cy, Math.max(bw, bh));
    r.addColorStop(0, `rgba(255,255,255,${a})`); r.addColorStop(1, 'rgba(255,255,255,0)');
    g.save(); g.translate(cx, cy); g.scale(1, bh / bw); g.translate(-cx, -cy);
    g.fillStyle = r; g.beginPath(); g.arc(cx, cy, bw, 0, Math.PI * 2); g.fill(); g.restore();
  };
  softbox(0.14 * w, 0.36 * h, 74, 300, 1.0);
  softbox(0.60 * w, 0.32 * h, 54, 280, 1.0);
  softbox(0.86 * w, 0.40 * h, 110, 180, 0.40);
  softbox(0.36 * w, 0.30 * h, 44, 220, 0.62);
  const tex = new THREE.Texture(c);
  tex.mapping = THREE.EquirectangularReflectionMapping;
  tex.colorSpace = THREE.SRGBColorSpace; tex.needsUpdate = true;
  const pm = new THREE.PMREMGenerator(renderer);
  const out = pm.fromEquirectangular(tex).texture;
  pm.dispose(); tex.dispose();
  return out;
}
